import type { ScheduleEntry, TimePeriod } from './schedule'

export type ScheduleConflictLocation = {
  kind: 'zone' | 'blind' | 'stand' | 'custom'
  id: string
  name?: string
}

export type ScheduleConflictCollision = {
  date: string
  location: ScheduleConflictLocation
}

export type ScheduleConflictPeer = {
  id: string
  name?: string
  createdAt: string
  isEarlier: boolean
  collisions: ScheduleConflictCollision[]
}

export type ScheduleConflict = {
  location: ScheduleConflictLocation
  peers: ScheduleConflictPeer[]
  heldFirstBy?: ScheduleConflictPeer
}

export function entryLocation(entry: ScheduleEntry): ScheduleConflictLocation | null {
  if (entry.zoneId) return { kind: 'zone', id: entry.zoneId, name: entry.zoneName }
  if (entry.blindId) return { kind: 'blind', id: entry.blindId, name: entry.blindName }
  if (entry.standId) return { kind: 'stand', id: entry.standId, name: entry.standName }
  if (entry.customLocationId) {
    return { kind: 'custom', id: entry.customLocationId, name: entry.customLocationName }
  }
  return null
}

export function entrySpanDates(entry: ScheduleEntry): string[] {
  const days = Math.max(1, entry.durationDays ?? 1)
  if (days === 1) return [entry.scheduleDate]
  const start = new Date(`${entry.scheduleDate}T00:00:00Z`)
  if (Number.isNaN(start.getTime())) return [entry.scheduleDate]
  const dates: string[] = []
  for (let offset = 0; offset < days; offset += 1) {
    const day = new Date(start.getTime() + offset * 86_400_000)
    dates.push(day.toISOString().slice(0, 10))
  }
  return dates
}

function effectivePeriod(entry: ScheduleEntry): TimePeriod | undefined {
  return entry.effectiveTimePeriod ?? entry.timePeriod
}

function dailyFor(entry: ScheduleEntry, date: string) {
  const index = entrySpanDates(entry).indexOf(date)
  if (index < 0) return undefined
  return (entry.dailyTimes ?? []).find((daily) => daily.day === index)
}

export function dayPeriod(entry: ScheduleEntry, date: string): TimePeriod | undefined {
  return dailyFor(entry, date)?.timePeriod ?? effectivePeriod(entry)
}

export function dayLocation(
  entry: ScheduleEntry,
  date: string,
): ScheduleConflictLocation | null {
  const daily = dailyFor(entry, date)
  if (daily?.blindId) return { kind: 'blind', id: daily.blindId, name: daily.blindName }
  if (daily?.standId) return { kind: 'stand', id: daily.standId, name: daily.standName }
  if (daily?.customLocationId) {
    return { kind: 'custom', id: daily.customLocationId, name: daily.customLocationName }
  }
  if (daily?.zoneId) return { kind: 'zone', id: daily.zoneId, name: daily.zoneName }
  return entryLocation(entry)
}

function dayPeriodsOverlap(
  left: TimePeriod | undefined,
  right: TimePeriod | undefined,
): boolean {
  if (!left || !right) return true
  if (left === 'all-day' || right === 'all-day') return true
  return left === right
}

export function collisionsBetween(
  entry: ScheduleEntry,
  other: ScheduleEntry,
): ScheduleConflictCollision[] {
  if (other.id === entry.id) return []
  if (entry.status === 'cancelled' || other.status === 'cancelled') return []
  if (entry.type !== 'hunt' || other.type !== 'hunt') return []

  const otherDays = new Set(entrySpanDates(other))
  const collisions: ScheduleConflictCollision[] = []
  for (const date of entrySpanDates(entry)) {
    if (!otherDays.has(date)) continue
    const here = dayLocation(entry, date)
    const there = dayLocation(other, date)
    if (!here || !there) continue
    if (here.kind !== there.kind || here.id !== there.id) continue
    if (!dayPeriodsOverlap(dayPeriod(entry, date), dayPeriod(other, date))) continue
    collisions.push({ date, location: here })
  }
  return collisions
}

function conflictFromPeers(peers: ScheduleConflictPeer[]): ScheduleConflict | undefined {
  if (peers.length === 0) return undefined
  const ordered = [...peers].sort((a, b) => a.createdAt.localeCompare(b.createdAt))
  const location = ordered[0].collisions[0]?.location
  if (!location) return undefined
  return {
    location,
    peers: ordered,
    heldFirstBy: ordered.find((peer) => peer.isEarlier),
  }
}

export function findScheduleConflicts(
  entries: ScheduleEntry[],
): Map<string, ScheduleConflict> {
  const byEntryId = new Map<string, ScheduleConflict>()

  for (const entry of entries) {
    const peers: ScheduleConflictPeer[] = []
    for (const other of entries) {
      const collisions = collisionsBetween(entry, other)
      if (collisions.length === 0) continue
      peers.push({
        id: other.id,
        name: other.name,
        createdAt: other.createdAt,
        isEarlier: other.createdAt < entry.createdAt,
        collisions,
      })
    }

    const conflict = conflictFromPeers(peers)
    if (conflict) byEntryId.set(entry.id, conflict)
  }

  return byEntryId
}

export function conflictDatesExcept(
  conflict: ScheduleConflict | undefined,
  date: string | undefined,
): string[] {
  if (!conflict) return []
  const dates = new Set<string>()
  for (const peer of conflict.peers) {
    for (const collision of peer.collisions) {
      if (collision.date !== date) dates.add(collision.date)
    }
  }
  return [...dates].sort()
}

export function conflictLocationOnDate(
  conflict: ScheduleConflict | undefined,
  date: string,
): ScheduleConflictLocation | null {
  if (!conflict) return null
  for (const peer of conflict.peers) {
    for (const collision of peer.collisions) {
      if (collision.date === date) return collision.location
    }
  }
  return null
}

export function conflictOnDate(
  conflict: ScheduleConflict | undefined,
  date: string | undefined,
): ScheduleConflict | undefined {
  if (!conflict || !date) return conflict
  const peers = conflict.peers
    .map((peer) => ({
      ...peer,
      collisions: peer.collisions.filter((collision) => collision.date === date),
    }))
    .filter((peer) => peer.collisions.length > 0)
  return conflictFromPeers(peers)
}
